/**
 * Ngưỡng cảnh báo vận hành của phòng khám (#19867 / STAT-34). Hai ô; để TRỐNG = tắt cảnh báo đó —
 * không có ngưỡng mặc định nào, vì một con số đoán sẵn sẽ báo động theo ý người viết mã chứ không
 * theo phòng khám. Lỗi của server (ngoài khoảng) hiện đúng câu server trả.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiError } from "../../shared/api/client";
import { reportsApi, reportsQueryKeys, type AlertSettings } from "../../shared/api/reports";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const toField = (v: number | null | undefined) => (v == null ? "" : String(v));
const fromField = (s: string): number | null => {
  const t = s.trim().replace(",", ".");
  return t === "" ? null : Number(t);
};

/**
 * Kiểm TRƯỚC khi gửi (review #19867): "20p" thành NaN, JSON gửi NaN là null, và server lưu "chưa
 * đặt" — cảnh báo bị TẮT trong im lặng trong khi người dùng tưởng đã đặt. Số phút phải là số nguyên
 * (server nhận Integer và cắt 20,5 thành 20 mà không báo).
 */
function invalidReason(wait: number | null, rise: number | null): string | null {
  if (wait !== null && (!Number.isInteger(wait) || wait < 1 || wait > 480)) {
    return "Ngưỡng thời gian chờ phải là số phút nguyên từ 1 đến 480.";
  }
  if (rise !== null && (!Number.isFinite(rise) || rise <= 0 || rise > 100)) {
    return "Ngưỡng tăng tỷ lệ vắng phải là một số lớn hơn 0 và tối đa 100.";
  }
  return null;
}

export function AlertSettingsDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const current = useQuery({
    queryKey: reportsQueryKeys.alertSettings(),
    queryFn: () => reportsApi.alertSettings(),
  });
  if (!current.isSuccess) {
    return (
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Ngưỡng cảnh báo</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {current.isError ? "Không tải được ngưỡng hiện tại." : "Đang tải…"}
          </p>
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <SettingsForm
      initial={current.data}
      onClose={onClose}
      onSaved={() => {
        void qc.invalidateQueries({ queryKey: reportsQueryKeys.alertSettings() });
        void qc.invalidateQueries({ queryKey: reportsQueryKeys.alerts() });
      }}
    />
  );
}

function SettingsForm({
  initial,
  onClose,
  onSaved,
}: {
  initial: AlertSettings;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [wait, setWait] = useState(toField(initial.waitMedianMinutes));
  const [rise, setRise] = useState(toField(initial.noShowRisePp));
  const [localError, setLocalError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      reportsApi.updateAlertSettings({
        waitMedianMinutes: fromField(wait),
        noShowRisePp: fromField(rise),
      }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[440px]">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const reason = invalidReason(fromField(wait), fromField(rise));
            setLocalError(reason);
            if (!reason) save.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>Ngưỡng cảnh báo</DialogTitle>
          </DialogHeader>
          <p className="mt-2 text-sm text-muted-foreground">
            Để trống một ô để tắt cảnh báo đó. Cảnh báo &quot;bệnh án chưa ký&quot; luôn bật.
          </p>
          <div className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="alert-wait">Thời gian chờ khám trung vị hôm nay vượt (phút)</Label>
              <Input
                id="alert-wait"
                inputMode="numeric"
                value={wait}
                onChange={(e) => setWait(e.target.value)}
                placeholder="Ví dụ 20 — để trống là tắt"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="alert-rise">
                Tỷ lệ vắng 7 ngày qua tăng hơn 4 tuần trước quá (điểm %)
              </Label>
              <Input
                id="alert-rise"
                inputMode="decimal"
                value={rise}
                onChange={(e) => setRise(e.target.value)}
                placeholder="Ví dụ 5 — để trống là tắt"
              />
            </div>
          </div>
          {(localError || save.isError) && (
            <p className="mt-3 text-sm text-destructive" role="alert">
              {localError ?? apiError(save.error).detail}
            </p>
          )}
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Huỷ
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? "Đang lưu…" : "Lưu"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
